import { getDefaultStore, useSetAtom, type createStore } from 'jotai';
import { useCallback, useState } from 'react';
import { type LibraryItem } from '../../../contracts';
import { useRemoveDocumentFromCollection } from '../../../data';
import { getDocumentDownloadUrl } from '../../../data/library/user-documents/api';
import { useRetryJob } from '../../../data/library/user-documents/hooks/useRetryJob';
import { documentPreviewAtom } from '../user-documents/atoms';
import { useDeleteUserDocument } from '../user-documents/hooks/useDeleteUserDocument';

type JotaiStore = ReturnType<typeof createStore>;

export function clearLibraryDocumentPreview(store: JotaiStore = getDefaultStore()): void {
  store.set(documentPreviewAtom, { isOpen: false, documentId: null });
}

export function useDocumentItemState(
  item: LibraryItem,
  selectedCollectionId: string | null
) {
  const setPreviewState = useSetAtom(documentPreviewAtom);
  const { mutate: deleteDocument, isPending: isDeleting } = useDeleteUserDocument();
  const { mutate: retryJob, isPending: isRetrying } = useRetryJob();
  const { mutate: removeFromCollection, isPending: isRemoving } =
    useRemoveDocumentFromCollection();
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const fileExtension = item.filename.split('.').pop()?.toUpperCase() ?? 'FILE';
  const isTeamDoc = item.scope === 'team';
  const isPersonalDoc = item.scope === 'user';
  const isProcessing = !['completed', 'failed'].includes(item.status);
  const isCompleted = item.status === 'completed';
  const isFailed = item.status === 'failed';
  const canAddToCollection = item.type === 'document' && isCompleted;
  // Client-only optimistic upload rows have no server id yet — Cancel would 404.
  const isClientOptimistic = item.id.startsWith('optimistic-');
  const canCancelOrDelete = !isClientOptimistic;

  const handleDelete = () => deleteDocument(item.id);
  const handleRetry = () => retryJob(item.id);
  const handleRemoveFromCollection = () => {
    if (selectedCollectionId) {
      removeFromCollection({ collectionId: selectedCollectionId, documentId: item.id });
    }
  };

  const handlePreview = useCallback(() => {
    setPreviewState({ isOpen: true, documentId: item.id });
  }, [item.id, setPreviewState]);

  const handleDownload = useCallback(() => {
    if (isDownloadLoading) return;
    setIsDownloadLoading(true);
    getDocumentDownloadUrl(item.id)
      .then((data) => {
        window.open(data.url, '_blank');
      })
      .catch((error) => {
        console.error('Failed to get download URL:', error);
      })
      .finally(() => {
        setIsDownloadLoading(false);
      });
  }, [item.id, isDownloadLoading]);

  return {
    fileExtension,
    isTeamDoc,
    isPersonalDoc,
    isProcessing,
    isCompleted,
    isFailed,
    canAddToCollection,
    canCancelOrDelete,
    isDeleting,
    isRetrying,
    isRemoving,
    isDownloadLoading,
    handleDelete,
    handleRetry,
    handleRemoveFromCollection,
    handlePreview,
    handleDownload,
  };
}
